import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { iframeResize } from "iframe-resizer";
import { TurnrCard } from "~/components/card/TurnrCard.jsx";
import { ServiceErrorAlert } from "~/components/Error.jsx";
import { TurnrErrorAlert } from "~/components/alerts/TurnrAlert.jsx";
import TurnrLink from "~/components/links/TurnrLink.jsx";
import { SkeletonLoaderWrapper } from "~/components/loading/skeletons/SkeletonLoaderWrapper.jsx";

const METABASE_ORIGIN = "https://metabase.ingrid.com";

// A successful embed posts a "location" message which clears the skeleton. A Metabase *error* page (e.g. an
// invalid token, or a locked dashboard parameter missing from the JWT) never posts it, so without this fallback
// the skeleton would spin forever and hide the error. After the iframe fires `load` we wait this long for the
// message; if it doesn't arrive we assume the embed failed, reveal the iframe so the Metabase error is visible,
// and show a banner with a direct link.
const LOAD_CONFIRMATION_TIMEOUT_MS = 8000;

// The signed embed url comes from the backend with the date baked into its token, so changing the date
// produces a brand-new url and forces the iframe to remount - which wipes any native Metabase filters the
// user had selected. We capture those native filter values from Metabase's "location" postMessage and
// re-apply them onto the freshly signed url so they survive the remount.
function mergeMetabaseFilters(signedUrl, capturedSearch) {
    if (!signedUrl || !capturedSearch) return signedUrl;
    try {
        const url = new URL(signedUrl);
        const captured = new URLSearchParams(capturedSearch);
        captured.forEach((value, key) => url.searchParams.set(key, value));
        return url.toString();
    } catch {
        return signedUrl;
    }
}

export default function MetabaseIframeCard({ url, children, error, serviceData, toolbarStyle }) {
    const iframeRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    // True when the load-confirmation message never arrived - we then assume the embed errored.
    const [loadFailed, setLoadFailed] = useState(false);
    const capturedFiltersRef = useRef("");
    const [effectiveUrl, setEffectiveUrl] = useState(null);

    // When a freshly signed url arrives (e.g. the date changed), re-apply the native filter values the user
    // had before the remount.
    useEffect(() => {
        setEffectiveUrl(mergeMetabaseFilters(url, capturedFiltersRef.current));
    }, [url]);

    useEffect(() => {
        setIsLoading(true);
        setLoadFailed(false);
    }, [effectiveUrl]);

    useEffect(() => {
        if (iframeRef.current) {
            iframeResize({ log: false, checkOrigin: false, heightCalculationMethod: "max" }, iframeRef.current);
        }
    }, [iframeRef?.current, effectiveUrl]);

    useEffect(() => {
        function handleMessage(event) {
            if (event?.origin !== METABASE_ORIGIN) return;
            if (!_.isObject(event?.data)) return;
            if (event?.data?.metabase?.type === "location") {
                setIsLoading(false);
                setLoadFailed(false);
                const location = event?.data?.metabase?.location;
                if (location?.search) capturedFiltersRef.current = location.search;
            }
        }
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // Fallback so a failed embed surfaces instead of spinning forever: while loading, wait for the confirmation
    // message; if it doesn't arrive, mark the load as failed (which reveals the iframe + an error banner). A late
    // success message still clears it via the handler above.
    useEffect(() => {
        if (!effectiveUrl || !isLoading) return undefined;
        const timeoutId = setTimeout(() => setLoadFailed(true), LOAD_CONFIRMATION_TIMEOUT_MS);
        return () => clearTimeout(timeoutId);
    }, [effectiveUrl, isLoading]);

    const showSkeleton = isLoading && !loadFailed;

    return (
        <TurnrCard>
            <div style={{ zIndex: 0, position: "relative", width: "100%" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", padding: "10px", marginBottom: "-10px", marginRight: "65px", ...toolbarStyle }}>{children}</div>
            </div>
            <ServiceErrorAlert serviceData={serviceData} errorText={error} />
            {loadFailed && (
                <TurnrErrorAlert fullWidth={true} title={<b>The dashboard could not be loaded</b>}>
                    Metabase did not confirm the dashboard loaded - it likely returned an error (for example an invalid token or a locked dashboard filter missing from the request).{" "}
                    {effectiveUrl && (
                        <span>
                            <TurnrLink href={effectiveUrl} target="_blank" rel="noopener noreferrer">
                                Open the dashboard directly
                            </TurnrLink>{" "}
                            to see the underlying Metabase error.
                        </span>
                    )}
                </TurnrErrorAlert>
            )}
            <iframe
                ref={iframeRef}
                key={effectiveUrl}
                style={{ overflow: "visible", border: "none", maxWidth: "1048", display: showSkeleton ? "none" : "initial" }}
                src={effectiveUrl}
                width={"100%"}
            />
            {showSkeleton && <SkeletonLoaderWrapper loading={showSkeleton} loadingMinHeight={300} slim={false} style={{ marginTop: "50px", position: "absolute" }} />}
        </TurnrCard>
    );
}
